/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { IonButton, IonIcon, IonModal, IonSpinner } from "@ionic/react";
import FollowerListItem from "./FollowerListItem";
import OrgFollowerRow from "./OrgFollowerRow";
import { orgDisplayName } from "./useOrgSharing";
import EmptyState from "./EmptyState";
import { requestTitle } from "./requestTitle";

import { FollowersListProps } from "./DataSharingTypes";
import { add, closeOutline } from "ionicons/icons";
import { DisplayPendingRequest } from "../PendingRequests";
import {
  acceptFollowRequest,
  rejectFollowRequest,
} from "../../../services/datashare";
import { getInitials, getUserName } from "../../../utils/helpers";

const FollowersList: React.FC<FollowersListProps> = ({
  followers,
  pendingIncoming,
  actionRequired,
  pendingFollowerRequests = [],
  onRemoveFollower,
  onAcceptRequest,
  onRejectRequest,
  onCancelRequest,
  onInviteClick,
  isLoading,
  connectedOrgs = [],
  pendingOrgs = [],
  onRemoveOrg,
  orgNameById,
  orgBusyId,
}) => {
  const [pendingOpen, setPendingOpen] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [actionsRequiredItems, setActionsRequiredItems] = useState<
    DisplayPendingRequest[]
  >(actionRequired || []);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  useEffect(() => {
    setActionsRequiredItems(actionRequired || []);
  }, [actionRequired]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <IonSpinner name="crescent" color="primary" />
      </div>
    );
  }

  const confirmAcceptRequest = async (selectedRequest?: any): Promise<void> => {
    if (!selectedRequest) return;
    setActionsRequiredItems((prev) =>
      prev.filter((req) => req.id !== selectedRequest.id)
    );
    if (selectedRequest.type === "incoming_follower_request") {
      await acceptFollowRequest({ relationship_id: selectedRequest.id });
    } else if (selectedRequest.type === "incoming_following_request") {
      await acceptFollowRequest({ relationship_id: selectedRequest.id });
    }
  };
  const confirmRejectRequest = async (selectedRequest?: any): Promise<void> => {
    if (!selectedRequest) return;
    try {
      setActionsRequiredItems((prev) =>
        prev.filter((req) => req.id !== selectedRequest.id)
      );
      await rejectFollowRequest({ relationship_id: selectedRequest.id });
    } catch (error) {
      console.error("Failed to reject request:", error);
    }
  };
  const followerRequests: DisplayPendingRequest[] = [];
  const followingRequests: DisplayPendingRequest[] = [];
  actionsRequiredItems.forEach((req) => {
    if (req.type === "incoming_follower_request") followerRequests.push(req);
    if (req.type === "incoming_following_request") followingRequests.push(req);
  });
  const pageType = "followers"; // or "following" based on context
  let filteredRequests: DisplayPendingRequest[] = [];
  const normalizedPageType = (pageType || "").toLowerCase();
  if (normalizedPageType === "followers") {
    filteredRequests = followerRequests.filter(Boolean);
  } else if (normalizedPageType === "following") {
    filteredRequests = followingRequests.filter(Boolean);
  } else {
    // default: include both incoming follower and following requests
    filteredRequests = actionsRequiredItems.filter(
      (req) =>
        req.type === "incoming_follower_request" ||
        req.type === "incoming_following_request"
    );
  }
  return (
    // F-469: no scroll box of its own — the Data Sharing screen scrolls as one, so this list can
    // never slide under the organizations section that follows it.
    <div
      className="space-y-2 pb-2"
      style={{
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <>
        {popupOpen && (
          <IonModal
            isOpen={popupOpen}
            onDidDismiss={() => setPopupOpen(false)}
            presentingElement={document.querySelector("ion-app") || undefined}
            canDismiss={true}
            showBackdrop={true}
            backdropDismiss={true}
            style={{
              "--background": "transparent",
              "--box-shadow": "none",
              "--border-radius": "20px",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "white",
                borderRadius: "20px",
                maxHeight: "70vh",
                overflow: "hidden",
                margin: "20px",
              }}
            >
              {/* Header with close button and title */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  margin: "3px",
                }}
              >
                <div style={{ width: "40px" }}></div>
                <h2
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    margin: "0",
                    color: "#000",
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  {selectedProfile?.first_name && selectedProfile?.last_name
                    ? `${selectedProfile.first_name} ${selectedProfile.last_name}`
                    : selectedProfile?.first_name || ""}{" "}
                </h2>
                <br />

                <button
                  onClick={() => setPopupOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    padding: "8px",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "top",
                    justifyContent: "top",
                  }}
                >
                  <IonIcon icon={closeOutline} style={{ fontSize: "24px" }} />
                </button>
              </div>
              <p className=" flex text-xl text-center items-center justify-center">
                {requestTitle(selectedProfile)}
              </p>
              <small
                className="text-gray-500"
                style={{
                  textAlign: "center",
                  justifyContent: "center",
                  display: "flex",
                }}
              >
                {(() => {
                  const now = new Date();
                  // Parse created_at as local time if no timezone, otherwise as UTC
                  const created = new Date(selectedProfile?.created_at);
                  const diff = Math.floor(
                    (now.getTime() - created.getTime()) / 1000
                  );
                  if (diff < 60) return "just now";
                  if (diff < 3600)
                    return `${Math.floor(diff / 60)} minutes ago`;
                  if (diff < 86400)
                    return `${Math.floor(diff / 3600)} hours ago`;
                  return `${Math.floor(diff / 86400)} days ago`;
                })()}
              </small>
              <div className=" flex text-xl text-center  mb-6  items-center justify-center">
                {selectedProfile?.email || ""}
                <br />
                {selectedProfile?.phone || ""}
              </div>
              {/* Message and simple action (replaces dynamic/undefined button rendering) */}
              <div style={{ padding: "0 16px 16px" }}>
                <div
                  style={{
                    padding: "0 ",
                    display: "flex",
                    gap: 12,
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <IonButton
                    expand="block"
                    fill={"solid"}
                    onClick={() => {
                      const action = async (selectedProfile: any) => {
                        setPopupOpen(false);
                        await confirmAcceptRequest(selectedProfile);
                      };
                      action(selectedProfile);
                    }}
                    className={"primary-button !w-[275px]"}
                    style={{
                      "--border-radius": "30px",
                      fontSize: "18px",
                      fontWeight: "500",
                    }}
                  >
                    {" "}
                    Accept{" "}
                  </IonButton>

                  <IonButton
                    expand="block"
                    fill={"outline"}
                    onClick={() => {
                      const action = async (selectedProfile: any) => {
                        setPopupOpen(false);
                        await confirmRejectRequest(selectedProfile);
                      };
                      action(selectedProfile);
                    }}
                    className={"tertiary-button !w-[275px]"}
                    style={{
                      "--border-radius": "30px",
                      fontSize: "18px",
                      fontWeight: "500",
                      "--color": "#ffffff !important",
                      borderRadius: "30px",
                    }}
                  >
                    Reject
                  </IonButton>
                </div>
              </div>
            </div>
          </IonModal>
        )}{" "}
        {filteredRequests.length > 0 && (
          <div>
            <div className="flex items-center ">
              <p className="sub-section-title">Pending Requests</p>
            </div>
            <div
              className=" "
              style={{
                overflowY: "auto",
                maxHeight: "100%",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {filteredRequests.length > 0 && (
                <div className="rounded-xl bg-white shadow-sm overflow-hidden sm:mx-0 border">
                  {pendingOpen && (
                    <div
                      id="pending-requests-list"
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      {filteredRequests.map((request) => (
                        <div key={`incoming-${request.id}`} className="p-1">
                          <div className="flex items-center justify-between p-2">
                            <div className="flex items-center">
                              <div
                                className="w-10 h-10 rounded-full bg-[#007C9E]    text-white flex items-center justify-center font-bold"
                                onClick={() => {
                                  setSelectedProfile(request);
                                  setPopupOpen(true);
                                }}
                              >
                                {getInitials(
                                  request.profile?.first_name,
                                  request.profile?.last_name
                                )}
                              </div>
                              <span
                                className="ml-3 font-medium truncate"
                                style={{ maxWidth: "120px" }}
                                onClick={() => {
                                  console.log("Selected profile:", request);
                                  setSelectedProfile(request);
                                  setPopupOpen(true);
                                }}
                              >
                                {getUserName(request.profile)}
                              </span>
                            </div>
                            <div className="flex items-center cursor-pointer text-[#0778CF]">
                              <p
                                className="px-1 py-1"
                                style={{
                                  fontFamily: "Figtree, sans-serif",
                                  fontWeight: 500,
                                  fontStyle: "normal",
                                  fontSize: "13px",
                                  lineHeight: "22px",
                                }}
                                onClick={() => confirmAcceptRequest(request)}
                              >
                                Accept
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Bottom spacing */}
              <div className="pb-4" />
            </div>
          </div>
        )}
      </>

      <div className=" sm:px-0">
        <div className="flex items-center justify-between">
          <p className="sub-section-title">Who can see my data</p>
          {(followers.length > 0 || connectedOrgs.length > 0 || pendingOrgs.length > 0) && (
            <button
              type="button"
              onClick={onInviteClick}
              className="flex items-center focus:outline-none bg-transparent border-none p-0"
              style={{ height: "24px", color: "#0778CF", cursor: "pointer" }}
              aria-label="Share my data"
            >
              <IonIcon
                icon={add}
                slot="icon-only"
                style={{ color: "#0778CF", fontSize: "18px" }}
              />
              <span
                className="ml-1"
                style={{
                  color: "#0778CF",
                  // fontSize: "15px",
                  fontWeight: 500,
                  lineHeight: "22px",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              >
                Share my data
              </span>
            </button>
          )}
        </div>
      </div>
      {/* Pending Incoming Requests Section with Accordion */}
      {(pendingIncoming.length > 0 || pendingFollowerRequests.length > 0) && (
        <div className="rounded-xl bg-white shadow-sm overflow-hidden sm:mx-0 border ">
          {/* <button
              className="w-full flex items-center justify-between p-4 bg-yellow-100 border-b  focus:outline-none"
              onClick={() => setPendingOpen((open) => !open)}
              aria-expanded={pendingOpen}
              aria-controls="pending-requests-list"
              style={{ cursor: "pointer" }}
            >
              <div>
                <h3 className="font-semibold text-blue-800 flex items-center">
                  Pending Requests ({pendingFollowerRequests.length})
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Waiting for them to approve
                </p>
              </div>
              <IonIcon
                icon={pendingOpen ? chevronDown : chevronForward}
                style={{ fontSize: "20px", color: "#B45309" }}
              />
            </button> */}
          {pendingOpen && (
            <div id="pending-requests-list" style={{ maxHeight: "100%" }}>
              {pendingFollowerRequests.length > 0 &&
                pendingFollowerRequests.map((request: any) => (
                  <div key={`sent-${request.id}`} className="p-1">
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-2"
                    >
                      <div className="flex items-center ">
                        <div className="w-10 h-10 rounded-full bg-[#007C9E] text-white flex items-center justify-center font-bold">
                          {getInitials(
                            request.profile?.first_name,
                            request.profile?.last_name
                          )}
                        </div>
                        <span
                          className="ml-3 font-medium truncate"
                          style={{ maxWidth: "120px" }}
                        >
                          {getUserName(request.profile)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <p
                          className="px-1 py-1 text-[#0778CF]"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelRequest(request);
                          }}
                          aria-label="Resend invitation"
                          style={{
                            fontFamily: "Figtree, sans-serif",
                            fontWeight: 500,
                            fontStyle: "normal",
                            fontSize: "13px",
                            lineHeight: "22px",
                            letterSpacing: "0px",
                          }}
                        >
                          Cancel Request
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

              {pendingIncoming.length > 0 && (
                <>
                  <p className="text-sm text-yellow-700 mt-1 px-4">
                    Requests Received to you
                  </p>
                  {pendingIncoming.map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-yellow-50 transition-colors"
                    >
                      <div className="flex items-center w-[50%]">
                        <div className="w-10 h-10 rounded-full bg-yellow-600 text-white flex items-center justify-center font-bold">
                          {getInitials(
                            request.profile?.first_name,
                            request.profile?.last_name
                          )}
                        </div>
                        <span
                          className="ml-3 font-medium truncate"
                          style={{ maxWidth: "120px" }}
                        >
                          {getUserName(request.profile)}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          className="px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors"
                          onClick={() => onAcceptRequest(request)}
                        >
                          Accept
                        </button>
                        <button
                          className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-400 transition-colors"
                          onClick={() => onRejectRequest(request)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
      {/* Current Followers Section — connected care organizations render at the TOP of the
          same list as the people who can see your data (ACDPA-186). */}
      {followers.length > 0 || connectedOrgs.length > 0 || pendingOrgs.length > 0 ? (
        <div
          className="bg-white rounded-xl shadow-sm overflow-hidden sm:mx-0"
          style={{
            width: "100%",
          }}
        >
          <div
            className="bg-white rounded-xl  overflow-hidden sm:mx-0"
            style={{
              width: "100%",
              boxShadow: "0px 8px 64px 0px #0000001A",
            }}
          >
            {connectedOrgs.map((org) => (
              <OrgFollowerRow
                key={`org-${org.org_id}`}
                org={org}
                displayName={
                  orgNameById ? orgDisplayName(org, orgNameById) : org.name
                }
                onRemove={onRemoveOrg || (() => undefined)}
                disabled={orgBusyId === org.org_id}
              />
            ))}
            {pendingOrgs.map((org) => (
              <OrgFollowerRow
                key={`org-pending-${org.org_id}`}
                org={org}
                displayName={
                  orgNameById ? orgDisplayName(org, orgNameById) : org.name
                }
                onRemove={onRemoveOrg || (() => undefined)}
                disabled={orgBusyId === org.org_id}
              />
            ))}
            {followers.map((follower) => (
              <FollowerListItem
                key={follower.id}
                follower={follower}
                onRemove={onRemoveFollower as any}
                itemType="follower"
              />
            ))}
          </div>
        </div>
      ) : (
        <EmptyState
          title="Share my data"
          description='"Share my data" allows you to give someone access to monitor your daily treatments, making it easier for caregivers, family members, or home health providers to support your care.'
          buttonText="Share my data"
          onButtonClick={onInviteClick}
        />
      )}
      {/* Show empty state when only pending requests exist */}
      {/* {followers.length === 0 && pendingIncoming.length > 0 && !isLoading && (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No Active Followers
          </h3>
          <p className="text-gray-600 mb-4">
            Accept pending requests and invite new people to share your
            data.
          </p>
          <button
            onClick={onInviteClick}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
          >
            Send New Invite
          </button>
        </div>
      )} */}
    </div>
  );
};

export default FollowersList;
